import { type FormEvent, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ApiError, apiPost, setAccessToken } from "../api";
import { useAuth } from "../auth";
import type { ConsoleUser } from "../types";

interface SessionResponse {
  tokens: { access_token: string };
  user: ConsoleUser;
}

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { setSession } = useAuth();
  const shopSlug = params.get("shop") || "";
  const token = params.get("token") || "";
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (password !== confirm) {
      setErr("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const data = await apiPost<SessionResponse>("/auth/accept-invite", {
        shop_slug: shopSlug,
        token,
        password,
        display_name: displayName || undefined,
      });
      setAccessToken(data.tokens.access_token);
      setSession(data.user);
      nav("/", { replace: true });
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : "Could not accept the invitation.");
    } finally {
      setBusy(false);
    }
  };

  if (!token || !shopSlug) {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <p className="err">This invitation link is incomplete.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-screen">
      <form className="auth-card" onSubmit={submit}>
        <p className="auth-sub">Set your password to join the operations console.</p>
        <label>
          Your name
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </label>
        <label>
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
        </label>
        <label>
          Confirm password
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
          />
        </label>
        <p className="auth-hint">
          At least 12 characters, with an uppercase letter, a number, and a symbol.
        </p>
        {err && <p className="err">{err}</p>}
        <button type="submit" className="primary" disabled={busy}>
          {busy ? "Setting up…" : "Set password & sign in"}
        </button>
      </form>
    </div>
  );
}
